/**
 * What this tab says when its session ends, and how the message reaches /home.
 *
 * A session that ends sends the tab to /home, which replaces the document and therefore the Redux
 * store the toast mechanism reads. The message travels in sessionStorage so the replacement document
 * can show it as a toast. Browser storage is sometimes the very thing that failed, so every access
 * here is best effort: a lost message never blocks the move to /home.
 *
 * @module client/Auth/sessionNotice
 */
import Alert from '@/lib/alert';

/** The app's landing route. `/` renders the same Home component, so it counts as home too. */
export const HOME_PATH = '/home';
export const isHomePath = (path: string): boolean => path === HOME_PATH || path === '/';

/** Routes that are already signing a user in. Navigating away from one abandons the sign-in. */
const SIGN_IN_PATHS = new Set(['/login', '/signup', '/callback', '/portalCallback']);
export const isSignInPath = (path: string): boolean => SIGN_IN_PATHS.has(path);

const NOTICE_KEY = 'outlaw:session-end-notice';

export interface SessionNotice {
    message: string;
    level: 'info' | 'warning' | 'error';
}

export const SESSION_ENDED_MESSAGE =
    'Your session ended. Your queued work stays on this device. Sign in again to continue.';

export const STORAGE_UNAVAILABLE_MESSAGE =
    'Browser storage is unavailable, so this tab cannot keep you signed in. Keep this tab open to ' +
    'preserve unfinished work, and check your browser’s storage settings.';

/** Suspension is deliberate, so it is a warning rather than an error: no exception is captured. */
export const SUSPENSION_NOTICE: SessionNotice = {
    level: 'warning',
    message: 'Your account was suspended, so this tab was signed out. Contact your firm administrator to restore access.',
};

export const sessionEndNotice = (storageFailed: boolean): SessionNotice => storageFailed
    ? {level: 'error', message: STORAGE_UNAVAILABLE_MESSAGE}
    : {level: 'info', message: SESSION_ENDED_MESSAGE};

export function raiseNotice(notice: SessionNotice): void {
    if (notice.level === 'error') Alert.addSimpleError(notice.message, 10_000);
    else if (notice.level === 'warning') Alert.add(new Alert(Alert.WARNING, null, 0, notice.message, 10_000));
    else Alert.addSimpleInfo(notice.message, 10_000);
}

/**
 * Leave this route for /home, carrying the reason across the navigation when we can. The navigation
 * is injectable so a test can observe the destination without replacing the test document.
 */
export function goHome(notice: SessionNotice, navigate: (path: string) => void = path => window.location.replace(path)): void {
    setSessionNotice(notice);
    navigate(HOME_PATH);
}

/**
 * Record a reason for a later document to show, without navigating. Used when the session ends
 * somewhere other than the watcher's own redirect (suspension) and when a redirect is deferred
 * (a sign-in already in flight).
 */
export function setSessionNotice(notice: SessionNotice): void {
    try {
        window.sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice));
    }
    catch {
        // Storage unavailable. The tab still lands on /home; only the explanation is lost.
    }
}

/** Read and clear the notice left by the document this one replaced. */
export function takeSessionNotice(): SessionNotice | undefined {
    try {
        const raw = window.sessionStorage.getItem(NOTICE_KEY);
        if (raw === null) return undefined;
        window.sessionStorage.removeItem(NOTICE_KEY);
        const notice = JSON.parse(raw) as SessionNotice;
        if (notice && typeof notice.message === 'string' &&
            (notice.level === 'info' || notice.level === 'warning' || notice.level === 'error')) return notice;
    }
    catch {
        // Malformed or unreadable: treat as no notice.
    }
    return undefined;
}
